import InfiniteScroll from "react-infinite-scroll-component";
import { t } from "@/i18n-js/instance";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { Event } from "@circle-react/types";
import { LoaderRow } from "@circle-react-shared/LoaderRow";
import { Typography } from "@circle-react-uikit/Typography";
import { ListViewItem } from "../ListViewItem";
import { FeaturedEventItem } from "../ListViewItem/FeaturedEventItem";
import { EventsListByMonth } from "./EventsListByMonth";
import { filterEvents, getEventsByMonth } from "./helpers";

export interface EventsListProps {
  activeSort: string;
  events: Event[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isDraft: boolean;
  isNextEventEnabled: boolean;
  isPast: boolean;
  isThumbnailView: boolean;
  onPostChange: (event: Event) => void;
  onPostDestroy: (id: number) => void;
  pinnedEvent?: Event;
}

export const EventsList = ({
  isPast,
  pinnedEvent,
  events,
  isDraft,
  fetchNextPage,
  hasNextPage,
  onPostDestroy,
  onPostChange,
  activeSort,
  isNextEventEnabled,
  isThumbnailView,
}: EventsListProps) => {
  const isUpcomingEvents = !(isPast || isDraft);
  const { liveEvents, filteredEvents } = filterEvents(events, isUpcomingEvents);

  const eventsByMonth = getEventsByMonth(filteredEvents, activeSort);
  const [firstUpcomingEvents] = eventsByMonth.values();
  const nextEvent =
    isNextEventEnabled &&
    !pinnedEvent &&
    isUpcomingEvents &&
    firstUpcomingEvents?.[0];
  const isLiveEventsAvailable = liveEvents.length > 0;

  const renderLiveIndicator = () => (
    <div className="flex h-[18px] w-[18px] items-center rounded-full bg-[#96F8B7]">
      <div className="m-auto h-[6px] w-[6px] rounded-full bg-[#008A2F]" />
    </div>
  );

  return (
    <InfiniteScroll
      style={{ overflow: "initial" }}
      scrollThreshold={0.5}
      next={fetchNextPage}
      hasMore={hasNextPage}
      loader={<LoaderRow />}
      dataLength={events?.length || 0}
      scrollableTarget="panel"
      className="pb-6"
    >
      {isUpcomingEvents && isLiveEventsAvailable && (
        <div className="mb-9">
          <div className="mx-6 my-3 flex items-center space-x-4 md:mx-0">
            {renderLiveIndicator()}
            <Typography.TitleSm weight="bold">
              {t("events.now")}
            </Typography.TitleSm>
          </div>
          <div
            className={classNames(
              "border-primary bg-primary border-y md:rounded-2xl md:border",
            )}
            id="live-events-list"
          >
            {liveEvents.map(event => (
              <ListViewItem
                key={event.id}
                event={event}
                onDestroy={() => onPostDestroy(event.id)}
                onRsvp={() => onPostChange(event)}
                isThumbnailView={isThumbnailView}
              />
            ))}
          </div>
        </div>
      )}
      {pinnedEvent ? (
        <FeaturedEventItem
          key={pinnedEvent.id}
          onDestroy={() => onPostDestroy(pinnedEvent.id)}
          event={pinnedEvent}
          onRsvp={() => onPostChange(pinnedEvent)}
          isThumbnailView={isThumbnailView}
        />
      ) : (
        nextEvent && (
          <FeaturedEventItem
            key={nextEvent.id}
            onDestroy={() => onPostDestroy(nextEvent.id)}
            event={nextEvent}
            onRsvp={() => onPostChange(nextEvent)}
            isThumbnailView={isThumbnailView}
            isNextEvent
          />
        )
      )}
      {!isPast ? (
        <EventsListByMonth
          eventsByMonth={eventsByMonth}
          nextEvent={nextEvent}
          onEventDestroy={onPostDestroy}
          onEventChange={onPostChange}
          isThumbnailView={isThumbnailView}
        />
      ) : (
        <div
          className={classNames(
            "border-primary bg-primary overflow-hidden border-y sm:rounded-2xl sm:border",
          )}
          id="past-events-list"
        >
          {events.map(event => (
            <ListViewItem
              key={event.id}
              event={event}
              onDestroy={() => onPostDestroy(event.id)}
              onRsvp={() => onPostChange(event)}
              isThumbnailView={isThumbnailView}
            />
          ))}
        </div>
      )}
    </InfiniteScroll>
  );
};
